import { BaseEdge, EdgeLabelRenderer, EdgeProps, getSmoothStepPath, useReactFlow } from "@xyflow/react";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useDebounce } from "@quake/shared-ui/hooks/useDebounce";
import { Button } from "@quake/shared-ui/components/ui/button";
import TrashIcon from "lucide-react/dist/esm/icons/x";

type AnimatedEdgeProps = EdgeProps & {
  style?: CSSProperties;
  animated?: boolean;
  dashLength?: number;
  dashGap?: number;
  data?: {
    animated?: boolean;
    dashLength?: number;
    dashGap?: number;
  };
};

export default function AnimatedEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  sourcePosition,
  targetPosition,
  targetY,
  style = {},
  animated = true,
  sourceHandleId,
  dashLength = 8,
  dashGap = 4,
  markerEnd,
  data,
  selected = false,
}: AnimatedEdgeProps) {
  const reactFlow = useReactFlow();
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetY,
    targetX,
    targetPosition,
    borderRadius: 10,
  });
  const [showBall, setShowBall] = useState(true);
  const path = useDebounce(edgePath, 500, {
    onStart: () => setShowBall(false),
    onEnd: () => setShowBall(true),
  });
  // Get animation properties from data or use defaults
  const isAnimated = data?.animated ?? animated;
  const dashLen = data?.dashLength ?? dashLength;
  const gap = data?.dashGap ?? dashGap;

  const staticDashedStyle: CSSProperties = {
    ...style,
    strokeDasharray: `${dashLen} ${gap}`,
    stroke:
      sourceHandleId === "truthy" ? "#22c55e" : sourceHandleId === "falsy" ? "#ef4444" : style.stroke || "#3b82f6", // Default blue color
    strokeWidth: style.strokeWidth || 2,
    pointerEvents: "stroke",
  };

  return (
    <g id={id}>
      <BaseEdge path={edgePath} style={staticDashedStyle} markerEnd={markerEnd} markerWidth={12} markerHeight={12} />
      {isAnimated && showBall && <AnimatedBall id={id} edgePath={path} style={staticDashedStyle} />}
      {selected && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${(sourceX + targetX) / 2}px, ${(sourceY + targetY) / 2}px)`,
              pointerEvents: "all",
            }}
          >
            <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 shadow-md dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              <Button
                variant="ghost"
                size="xs"
                className="h-6 px-2"
                onClick={(event) => {
                  event.stopPropagation();
                  reactFlow.deleteElements({ edges: [{ id }] });
                }}
              >
                <TrashIcon className="h-3.5 w-3.5 text-red-700" />
              </Button>
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </g>
  );
}

export function AnimatedBall({
  id,
  edgePath,
  style,
  pointsPerSecond = 100,
}: {
  id: string;
  edgePath: string;
  style: CSSProperties;
  pointsPerSecond?: number;
}) {
  const [pathLength, setPathLength] = useState(0);
  const pathRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
    }
  }, [edgePath]);

  // Calculate duration based on path length and speed (200 points per second)
  const calculatedDuration = pathLength > 0 ? pathLength / pointsPerSecond : 1;
  const dur = `${Math.max(0.8, calculatedDuration)}s`;

  return (
    <>
      {/* Invisible path solely for motion reference */}
      <path ref={pathRef} id={`edge-motion-${id}`} d={edgePath} fill="none" stroke="none" pointerEvents="none" />

      <circle r={4} fill={style.stroke || "#3b82f6"} stroke="none" opacity={0}>
        {/* Move along the path */}
        <animateMotion dur={dur} repeatCount="indefinite" rotate="auto">
          <mpath href={`#edge-motion-${id}`} />
        </animateMotion>

        {/* Fade in (0-10%), stay (10-90%), fade out (90-100%) */}
        <animate attributeName="opacity" dur={dur} repeatCount="indefinite" keyTimes="0;0.1;0.9;1" values="0;1;1;0" />
      </circle>
    </>
  );
}
